import { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Popper, Typography, Zoom } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import type { StreamStateValue } from "./StreamState";

export type StreamPreviewPopoverOpenAnimation = "none" | "zoom";

type StreamPreviewPopoverProps = {
  streamId: string;
  title: string;
  streamState?: StreamStateValue;
  disabled?: boolean;
  previewWidth?: number;
  previewHeight?: number;
  placement?: "top" | "bottom";
  openAnimation?: StreamPreviewPopoverOpenAnimation;
  children: JSX.Element;
};

const DEFAULT_PREVIEW_WIDTH = 200;
const DEFAULT_PREVIEW_HEIGHT = 112;
const CLOSE_DELAY_MS = 120;
const OPEN_ZOOM_DURATION_MS = 300;
const DEFAULT_PREVIEW_REFRESH_INTERVAL_MS = 1000;
const HIDDEN_PREVIEW_REFRESH_INTERVAL_MS = 10000;
const PREVIEW_RETRY_BASE_MS = 1000;
const PREVIEW_RETRY_MAX_MS = 15000;
const PREVIEW_ERROR_DISPLAY_FAILURE_THRESHOLD = 3;

const warmImageObjectUrl = async (objectUrl: string): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("unable to decode preview image"));
    image.src = objectUrl;
  });
};

const StreamPreviewPopover = ({
  streamId,
  title,
  streamState,
  disabled = false,
  previewWidth = DEFAULT_PREVIEW_WIDTH,
  previewHeight = DEFAULT_PREVIEW_HEIGHT,
  placement = "top",
  openAnimation = "none",
  children
}: StreamPreviewPopoverProps) => {
  const { authorizedFetch, resolveApiPath } = useAuth();
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isPageHidden, setIsPageHidden] = useState(() => (typeof document !== "undefined" ? document.hidden : false));
  const failureCountRef = useRef(0);
  const previewUrlRef = useRef<string | null>(null);
  const lastModifiedRef = useRef<string | null>(null);
  const nextFetchAtRef = useRef(0);
  const nextRetryAtRef = useRef(0);
  const inFlightRef = useRef(false);
  const inFlightControllerRef = useRef<AbortController | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const effectiveRefreshInterval = isPageHidden ? HIDDEN_PREVIEW_REFRESH_INTERVAL_MS : DEFAULT_PREVIEW_REFRESH_INTERVAL_MS;
  const schedulerIntervalMs = Math.max(200, Math.min(1000, Math.floor(effectiveRefreshInterval / 4)));

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, CLOSE_DELAY_MS);
  };

  const open = isOpen && Boolean(anchorRef.current) && !disabled;
  const shouldAnimateOpen = openAnimation === "zoom";
  const popperPlacement = useMemo(
    () => (placement === "bottom" ? "bottom" : "top"),
    [placement]
  );
  const popoverTransformOrigin = popperPlacement === "bottom" ? "center top" : "center bottom";

  useEffect(() => {
    return () => {
      clearCloseTimer();
      if (inFlightControllerRef.current) {
        inFlightControllerRef.current.abort();
        inFlightControllerRef.current = null;
      }
      inFlightRef.current = false;
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      setIsPageHidden(document.hidden);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (inFlightControllerRef.current) {
      inFlightControllerRef.current.abort();
      inFlightControllerRef.current = null;
    }
    inFlightRef.current = false;
    failureCountRef.current = 0;
    nextFetchAtRef.current = 0;
    nextRetryAtRef.current = 0;
    lastModifiedRef.current = null;
  }, [streamId]);

  useEffect(() => {
    if (!open) {
      if (inFlightControllerRef.current) {
        inFlightControllerRef.current.abort();
        inFlightControllerRef.current = null;
      }
      inFlightRef.current = false;
      setIsLoading(false);
      setHasError(false);
      failureCountRef.current = 0;
      nextFetchAtRef.current = 0;
      nextRetryAtRef.current = 0;
      lastModifiedRef.current = null;
      setPreviewUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        previewUrlRef.current = null;
        return null;
      });
      return;
    }

    if (streamState === "timeout") {
      if (inFlightControllerRef.current) {
        inFlightControllerRef.current.abort();
        inFlightControllerRef.current = null;
      }
      inFlightRef.current = false;
      setHasError(false);
      setIsLoading(false);
      failureCountRef.current = 0;
      nextFetchAtRef.current = 0;
      nextRetryAtRef.current = 0;
      lastModifiedRef.current = null;
      setPreviewUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        previewUrlRef.current = null;
        return null;
      });
      return;
    }

    let active = true;
    const fetchPreview = async () => {
      if (inFlightRef.current) {
        return;
      }
      const now = Date.now();
      if (nextFetchAtRef.current > now || nextRetryAtRef.current > now) {
        return;
      }
      inFlightRef.current = true;
      const controller = new AbortController();
      inFlightControllerRef.current = controller;
      try {
        setIsLoading(true);
        setHasError(false);
        const previewUrl = resolveApiPath(`/api/streams/${streamId}/preview.jpg`);
        const ifModifiedSince =
          previewUrlRef.current && lastModifiedRef.current
            ? lastModifiedRef.current
            : null;
        const response = await authorizedFetch(previewUrl, {
          signal: controller.signal,
          headers: ifModifiedSince ? { "If-Modified-Since": ifModifiedSince } : undefined
        });
        if (response.status === 304) {
          failureCountRef.current = 0;
          nextRetryAtRef.current = 0;
          nextFetchAtRef.current = Date.now() + effectiveRefreshInterval;
          setHasError(false);
          setIsLoading(false);
          return;
        }
        if (!response.ok) {
          const error = new Error("preview unavailable") as Error & { status?: number };
          error.status = response.status;
          throw error;
        }
        const lastModified = response.headers.get("Last-Modified");
        if (lastModified) {
          lastModifiedRef.current = lastModified;
        }
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        try {
          await warmImageObjectUrl(objectUrl);
        } catch {
          URL.revokeObjectURL(objectUrl);
          throw new Error("preview decode failed");
        }
        if (!active) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreviewUrl((prev) => {
          if (prev) {
            URL.revokeObjectURL(prev);
          }
          previewUrlRef.current = objectUrl;
          return objectUrl;
        });
        failureCountRef.current = 0;
        nextRetryAtRef.current = 0;
        nextFetchAtRef.current = Date.now() + effectiveRefreshInterval;
        setIsLoading(false);
      } catch (error) {
        if (!active) {
          return;
        }
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const status =
          typeof error === "object" && error !== null && "status" in error
            ? Number((error as { status?: number }).status)
            : undefined;
        if (status === 404) {
          // Preview is not ready yet; keep showing loading progress.
          setHasError(false);
          setIsLoading(true);
          nextRetryAtRef.current = 0;
          nextFetchAtRef.current = Date.now() + effectiveRefreshInterval;
          return;
        }
        failureCountRef.current += 1;
        const retryDelay = Math.min(PREVIEW_RETRY_MAX_MS, PREVIEW_RETRY_BASE_MS * 2 ** Math.min(failureCountRef.current - 1, 4));
        const nextAttemptAt = Date.now() + retryDelay;
        nextRetryAtRef.current = nextAttemptAt;
        nextFetchAtRef.current = nextAttemptAt;
        if (failureCountRef.current < PREVIEW_ERROR_DISPLAY_FAILURE_THRESHOLD) {
          setHasError(false);
          setIsLoading(true);
          return;
        }
        setHasError(true);
        setIsLoading(false);
      } finally {
        if (inFlightControllerRef.current === controller) {
          inFlightControllerRef.current = null;
        }
        inFlightRef.current = false;
      }
    };

    void fetchPreview();
    const timer = window.setInterval(() => {
      void fetchPreview();
    }, schedulerIntervalMs);

    return () => {
      active = false;
      window.clearInterval(timer);
      if (inFlightControllerRef.current) {
        inFlightControllerRef.current.abort();
        inFlightControllerRef.current = null;
      }
      inFlightRef.current = false;
    };
  }, [authorizedFetch, effectiveRefreshInterval, open, resolveApiPath, schedulerIntervalMs, streamId, streamState]);

  const popoverBody = (
    <Box
      onMouseEnter={() => {
        clearCloseTimer();
      }}
      onMouseLeave={() => {
        scheduleClose();
      }}
      sx={{
        p: 0.2,
        width: previewWidth,
        bgcolor: "background.paper",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: 6
      }}
    >
      {/* <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        Preview
      </Typography>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }} noWrap>
        {title}
      </Typography> */}
      <Box
        sx={{
          width: "100%",
          height: previewHeight,
          bgcolor: "background.default",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          position: "relative"
        }}
      >
        {streamState === "timeout" ? (
          <Typography variant="body2" color="warning.main">
            Timeout
          </Typography>
        ) : previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt={title}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : isLoading || (!previewUrl && !hasError) ? (
          <CircularProgress size={22} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Preview unavailable
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <span
        ref={anchorRef}
        onMouseEnter={(event) => {
          if (disabled) {
            return;
          }
          clearCloseTimer();
          setIsOpen(true);
        }}
        onMouseLeave={() => {
          scheduleClose();
        }}
        style={{ display: "inline-flex" }}
      >
        {children}
      </span>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement={popperPlacement}
        keepMounted
        transition={shouldAnimateOpen}
        modifiers={[
          {
            name: "offset",
            options: { offset: [0, 8] }
          }
        ]}
      >
        {shouldAnimateOpen ? (
          ({ TransitionProps }) => (
            <Zoom
              {...TransitionProps}
              timeout={OPEN_ZOOM_DURATION_MS}
              style={{ transformOrigin: popoverTransformOrigin }}
            >
              {popoverBody}
            </Zoom>
          )
        ) : (
          popoverBody
        )}
      </Popper>
    </>
  );
};

export default StreamPreviewPopover;
